'use client';

import Link from 'next/link';
import type React from 'react';
import { ThemeSwitcherWrapper } from '@/app/_components/ThemeSwitcherWrapper';
import { useScrollContainer } from '@/app/_lib/providers/ScrollProvider';
import LogoSvg from './LogoSvg';

export function Footer() {
  const { scrollContainerRef } = useScrollContainer();

  // Use the public environment variables directly in client components
  const appName = process.env.NEXT_PUBLIC_APP_NAME || '';
  const appLegalName = process.env.NEXT_PUBLIC_APP_LEGAL_NAME || '';
  const facilityStreet = process.env.NEXT_PUBLIC_FACILITY_STREET || '';
  const facilityCity = process.env.NEXT_PUBLIC_FACILITY_CITY || '';
  const facilityState = process.env.NEXT_PUBLIC_FACILITY_STATE || '';
  const facilityZipcode = process.env.NEXT_PUBLIC_FACILITY_ZIPCODE || '';
  const facilityTelephone = process.env.NEXT_PUBLIC_FACILITY_TELEPHONE || '';
  const shortDescription = process.env.NEXT_PUBLIC_APP_SHORT_DESCRIPTION || '';
  const instagramUrl = process.env.NEXT_PUBLIC_INSTAGRAM_URL || '';
  const currYear = 2025;
  const formattedPhone = facilityTelephone.replace(/[^0-9]/g, '');

  // Validate Instagram URL
  const isValidInstagramUrl = (url: string) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname === 'instagram.com' || urlObj.hostname === 'www.instagram.com';
    } catch {
      return false;
    }
  };

  const scrollToTop = () => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <footer
      className='from-primary to-primary-dark relative overflow-hidden bg-gradient-to-b py-16 text-white md:py-24 dark:from-gray-800 dark:to-gray-900'
      aria-labelledby='footer-heading'
    >
      <h2 id='footer-heading' className='sr-only'>
        Footer
      </h2>

      {/* Background Pattern */}
      <div className='absolute inset-0 opacity-5' aria-hidden={true}>
        <div
          className='absolute inset-0'
          style={{
            backgroundImage:
              'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '30px 30px',
          }}
        />
      </div>

      <div className='relative z-10 container mx-auto px-4 sm:px-6'>
        <div className='grid grid-cols-1 gap-12 md:grid-cols-3 xl:grid-cols-3 xl:gap-8'>
          {/* Contact Info */}
          <div
            className='animate-when-visible text-center xl:text-left'
            style={{ '--animation-duration': '0.6s' } as React.CSSProperties}
            ref={(el) => {
              if (el) {
                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                      }
                    });
                  },
                  { threshold: 0.1 },
                );
                observer.observe(el);
              }
            }}
          >
            <h3 className='mb-4 font-serif text-2xl font-bold'>{appName}</h3>
            <address className='mb-6 text-lg text-white/90 not-italic'>
              <p className='mb-1'>{facilityStreet}</p>
              <p className='mb-1'>
                {facilityCity}, {facilityState} {facilityZipcode}
              </p>
            </address>

            <div className='xl:space-t-4 mt-6 flex flex-col items-center justify-center space-y-3 xl:items-start xl:justify-start'>
              <Link
                href={`https://maps.google.com/?q=${encodeURIComponent(`${facilityStreet}, ${facilityCity}, ${facilityState} ${facilityZipcode}`)}`}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center justify-center text-white/90 transition-colors duration-200 hover:text-white'
                aria-label='Get directions to our facility'
              >
                <svg className='mr-2 h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                  />
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                  />
                </svg>
                Get Directions
              </Link>

              {isValidInstagramUrl(instagramUrl) && (
                <Link
                  href={instagramUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='inline-flex items-center justify-center text-white/90 transition-colors duration-200 hover:text-white'
                  aria-label='Follow us on Instagram'
                >
                  <svg className='mr-2 h-5 w-5' fill='currentColor' viewBox='0 0 24 24'>
                    <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' />
                  </svg>
                  Follow Us
                </Link>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div
            className='animate-when-visible text-center'
            style={
              {
                '--animation-duration': '0.6s',
                '--animation-delay': '0.2s',
              } as React.CSSProperties
            }
            ref={(el) => {
              if (el) {
                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                      }
                    });
                  },
                  { threshold: 0.1 },
                );
                observer.observe(el);
              }
            }}
          >
            <h3 className='mb-4 font-serif text-2xl font-bold'>Call Us for Information!</h3>
            <a
              href={`tel:${formattedPhone}`}
              className='group text-primary dark:text-primary-light hover:bg-accent dark:hover:bg-primary-dark inline-flex transform items-center justify-center rounded-lg border border-transparent bg-white px-6 py-3 text-lg font-medium shadow-lg transition-all duration-300 hover:-translate-y-1 hover:text-black hover:shadow-xl hover:text-shadow-lg hover:text-shadow-yellow-200/20 *:hover:drop-shadow-lg *:hover:drop-shadow-yellow-200/20 dark:bg-gray-900 dark:hover:text-white dark:hover:text-shadow-gray-800/20 *:dark:hover:drop-shadow-gray-800/20'
              aria-label={`Call us at ${facilityTelephone}`}
            >
              <svg
                className='mr-2 h-5 w-5 group-hover:animate-bounce'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                />
              </svg>
              {facilityTelephone}
            </a>

            <div className='mt-8'>
              <h4 className='mb-4 font-serif text-xl font-semibold'>Quick Links</h4>
              <nav className='flex flex-col space-y-2' aria-label='Footer navigation'>
                <a href='#history' className='text-white/90 transition-colors duration-200 hover:text-white'>
                  History
                </a>
                <a href='#vision' className='text-white/90 transition-colors duration-200 hover:text-white'>
                  Vision
                </a>
                <a href='#locations' className='text-white/90 transition-colors duration-200 hover:text-white'>
                  Our Communities
                </a>
                <a href='#contact-us' className='text-white/90 transition-colors duration-200 hover:text-white'>
                  Contact Us
                </a>
                <a href='#jobs' className='text-white/90 transition-colors duration-200 hover:text-white'>
                  Job Opportunities
                </a>
              </nav>
            </div>
          </div>

          {/* Logo & Copyright */}
          <div
            className='animate-when-visible flex flex-col items-center lg:items-end'
            style={
              {
                '--animation-duration': '0.6s',
                '--animation-delay': '0.4s',
              } as React.CSSProperties
            }
            ref={(el) => {
              if (el) {
                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                      }
                    });
                  },
                  { threshold: 0.1 },
                );
                observer.observe(el);
              }
            }}
          >
            <div className='align-center mb-6 flex h-auto w-full justify-center lg:w-2/3'>
              <LogoSvg colorMode='all-light' />
            </div>

            <p className='mb-4 text-center text-lg text-white/90 lg:text-end'>{shortDescription}</p>

            <div className='mt-6 text-center text-sm text-white/80 lg:text-end'>
              &copy; {currYear} {appLegalName}
              <br />
              All rights reserved.
            </div>
          </div>
        </div>

        {/* Bottom Links */}
        <div
          className='animate-when-visible mt-12 border-t border-white/10 pt-8 text-center font-serif text-sm text-white/80'
          style={
            {
              '--animation-duration': '0.6s',
              '--animation-delay': '0.6s',
            } as React.CSSProperties
          }
          ref={(el) => {
            if (el) {
              const observer = new IntersectionObserver(
                (entries) => {
                  entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                      entry.target.classList.add('is-visible');
                      observer.unobserve(entry.target);
                    }
                  });
                },
                { threshold: 0.1 },
              );
              observer.observe(el);
            }
          }}
        >
          <div className='flex flex-col items-center justify-center space-y-4 md:flex-row md:space-y-0'>
            <Link href='/privacy-policy' className='mx-3 transition-colors duration-200 hover:text-white'>
              Privacy Policy
            </Link>
            <span className='mx-3 hidden md:inline'>•</span>
            <Link href='/terms-of-use' className='mx-3 transition-colors duration-200 hover:text-white'>
              Terms of Use
            </Link>
            <span className='mx-3 hidden md:inline'>•</span>
            <p className='mx-3'>
              <button
                type='button'
                onClick={scrollToTop}
                className='transition-colors duration-200 hover:text-white'
                aria-label='Back to top'
              >
                Back to Top
              </button>
            </p>
            <span className='mx-3 hidden md:inline'>•</span>
            <ThemeSwitcherWrapper />
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
